import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { ConciergeHomeMobileNavState, ConciergeHomeMobileNavResolvedIcons } from "./types";
import { themeColor } from "../../../../types/color";
import { ResolveColorFn } from "../../../../hooks/useColorResolver";

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

const defaultState: ConciergeHomeMobileNavState = {
  // Hamburger icon
  hamburgerIcon: "default",
  hamburgerIconSize: 32,
  hamburgerIconColor: themeColor("textPrimary"),

  // Close icon
  closeIcon: "default",
  closeIconSize: 32,
  closeIconColor: themeColor("textPrimary"),

  // Logo visibility
  showLogo: true,

  // Layout
  navHeight: 80,
  navPaddingX: 24,
  navPaddingY: 24,
  navBgColor: themeColor("transparent"),
};

// ---------------------------------------------------------------------------
// Edit fields
// ---------------------------------------------------------------------------

const editFields: EditField[] = [
  // Hamburger icon
  {
    key: "hamburgerIcon",
    label: "Menu Icon",
    type: "icon",
    group: "Menu Icon",
  },
  {
    key: "hamburgerIconSize",
    label: "Size",
    type: "number",
    min: 16,
    max: 48,
    step: 2,
    group: "Menu Icon",
  },
  {
    key: "hamburgerIconColor",
    label: "Color",
    type: "color",
    group: "Menu Icon",
  },

  // Close icon
  {
    key: "closeIcon",
    label: "Close Icon",
    type: "icon",
    group: "Close Icon",
  },
  {
    key: "closeIconSize",
    label: "Size",
    type: "number",
    min: 16,
    max: 48,
    step: 2,
    group: "Close Icon",
  },
  {
    key: "closeIconColor",
    label: "Color",
    type: "color",
    group: "Close Icon",
  },

  // Logo visibility
  {
    key: "showLogo",
    label: "Show Logo",
    type: "toggle",
    group: "Logo",
  },

  // Layout
  {
    key: "navHeight",
    label: "Height",
    type: "number",
    min: 40,
    max: 120,
    step: 4,
    group: "Layout",
  },
  {
    key: "navPaddingX",
    label: "Horizontal Padding",
    type: "number",
    min: 0,
    max: 48,
    step: 4,
    group: "Layout",
  },
  {
    key: "navPaddingY",
    label: "Vertical Padding",
    type: "number",
    min: 0,
    max: 48,
    step: 4,
    group: "Layout",
  },
  {
    key: "navBgColor",
    label: "Background",
    type: "color",
    group: "Layout",
  },
];

// ---------------------------------------------------------------------------
// Widget props
// ---------------------------------------------------------------------------

export interface ConciergeHomeMobileNavWidgetProps {
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ConciergeHomeMobileNavState>;
  /**
   * Render-prop: receives widget state, resolved icon URLs, and a color
   * resolver function. The consumer uses these to render the mobile nav UI.
   */
  children: (
    state: ConciergeHomeMobileNavState,
    icons: ConciergeHomeMobileNavResolvedIcons,
    resolveColor: ResolveColorFn,
  ) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Widget component
// ---------------------------------------------------------------------------

export const ConciergeHomeMobileNavWidget: React.FC<
  ConciergeHomeMobileNavWidgetProps
> = ({
  widgetId = "concierge-home-mobile-nav",
  displayName = "Mobile Nav Header",
  initialState = {},
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergeHomeMobileNavState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Resolve icons
  const hamburgerCustomIcon =
    state.hamburgerIcon && state.hamburgerIcon !== "default"
      ? themeIcons[state.hamburgerIcon]
      : null;
  const closeCustomIcon =
    state.closeIcon && state.closeIcon !== "default"
      ? themeIcons[state.closeIcon]
      : null;

  const resolvedIcons: ConciergeHomeMobileNavResolvedIcons = {
    hamburgerIconUrl: hamburgerCustomIcon ? hamburgerCustomIcon.url : null,
    closeIconUrl: closeCustomIcon ? closeCustomIcon.url : null,
  };

  if (isLoading) {
    return <></>;
  }

  const widgetContent = children(state, resolvedIcons, resolveColor);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeIcons={themeIcons}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
